import type { User } from '@/features/auth/AuthContext';
import type { ProjectNode } from '@/shared/types/projectData';

export type NodeAuthorityRole = 'subtree_owner' | 'editor' | 'viewer';
export type NodeAuthorityAction =
  | 'create_child'
  | 'update'
  | 'delete'
  | 'move'
  | 'reorder'
  | 'duplicate'
  | 'manage_policy';

export const authorityRoleRank: Record<NodeAuthorityRole, number> = {
  viewer: 0,
  editor: 1,
  subtree_owner: 2,
};

export const authorityPolicyRoleOptions: Array<'' | NodeAuthorityRole> = [
  '',
  'viewer',
  'editor',
  'subtree_owner',
];

export function nodeHasAuthorityPolicy(node?: Partial<ProjectNode> | null): boolean {
  const rawNode = node as (Partial<ProjectNode> & {
    authority_owner_id?: string | null;
    authority_entries?: Array<{ user_id?: string; userId?: string; role?: NodeAuthorityRole }>;
  }) | null | undefined;
  return Boolean(
    node?.authorityOwnerId
      || rawNode?.authority_owner_id
      || (node?.authorityEntries?.length || 0) > 0
      || (rawNode?.authority_entries?.length || 0) > 0
  );
}

function sameProject(left?: Partial<ProjectNode> | null, right?: Partial<ProjectNode> | null): boolean {
  const leftProjectId = left?.projectId;
  const rightProjectId = right?.projectId;
  return !leftProjectId || !rightProjectId || leftProjectId === rightProjectId;
}

function nodeAuthorityRoleForUser(node: Partial<ProjectNode>, userId?: string): NodeAuthorityRole | null {
  if (!userId) return null;
  const rawNode = node as Partial<ProjectNode> & {
    authority_owner_id?: string | null;
    authority_entries?: Array<{ user_id?: string; userId?: string; role?: NodeAuthorityRole }>;
  };
  const ownerId = node.authorityOwnerId || rawNode.authority_owner_id;
  let bestRole: NodeAuthorityRole | null = ownerId === userId ? 'subtree_owner' : null;
  const entries = [
    ...(node.authorityEntries || []),
    ...(rawNode.authority_entries || []),
  ] as Array<{ userId?: string; user_id?: string; role?: NodeAuthorityRole }>;
  for (const entry of entries) {
    const entryUserId = entry.userId || entry.user_id;
    if (entryUserId !== userId) continue;
    const role = entry.role;
    if (!role || !(role in authorityRoleRank)) continue;
    if (!bestRole || authorityRoleRank[role] > authorityRoleRank[bestRole]) {
      bestRole = role;
    }
  }
  return bestRole;
}

function getAncestorPath(
  nodes: ProjectNode[],
  node: Partial<ProjectNode> | null | undefined,
): Partial<ProjectNode>[] {
  if (!node?.id) return [];
  const result: Partial<ProjectNode>[] = [];
  const seen = new Set<string>();
  const relevantNodes = nodes.filter((candidate) => sameProject(candidate, node));
  const byId = new Map(relevantNodes.map((candidate) => [candidate.id, candidate]));
  let current: Partial<ProjectNode> | undefined = node;
  while (current?.id && !seen.has(current.id)) {
    seen.add(current.id);
    result.push(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return result;
}

function subtreeRoleAllows(role: NodeAuthorityRole | null, action: NodeAuthorityAction): boolean {
  if (role === 'subtree_owner') return true;
  if (role === 'editor') {
    return action !== 'manage_policy';
  }
  return false;
}

function authorizedPolicyNodeIds(
  nodes: ProjectNode[],
  node: Partial<ProjectNode> | null | undefined,
  userId: string | undefined,
  action: NodeAuthorityAction,
): Set<string> {
  const policyIds = new Set<string>();
  for (const ancestor of getAncestorPath(nodes, node)) {
    if (!ancestor.id || !nodeHasAuthorityPolicy(ancestor)) continue;
    const role = nodeAuthorityRoleForUser(ancestor, userId);
    if (subtreeRoleAllows(role, action)) {
      policyIds.add(ancestor.id);
    }
  }
  return policyIds;
}

export function getEffectiveNodeAuthorityRole(
  nodes: ProjectNode[],
  node: Partial<ProjectNode> | null | undefined,
  userId?: string,
): { role: NodeAuthorityRole | null; hasPolicyInPath: boolean } {
  let bestRole: NodeAuthorityRole | null = null;
  let hasPolicyInPath = false;
  for (const ancestor of getAncestorPath(nodes, node)) {
    if (!nodeHasAuthorityPolicy(ancestor)) continue;
    hasPolicyInPath = true;
    const role = nodeAuthorityRoleForUser(ancestor, userId);
    if (role && (!bestRole || authorityRoleRank[role] > authorityRoleRank[bestRole])) {
      bestRole = role;
    }
  }
  return { role: bestRole, hasPolicyInPath };
}

export function canActOnNode(params: {
  nodes: ProjectNode[];
  node?: Partial<ProjectNode> | null;
  action: NodeAuthorityAction;
  projectRole?: string;
  user?: Pick<User, 'id' | 'role'> | null;
}): boolean {
  const { nodes, node, action, projectRole, user } = params;
  if (user?.role === 'admin') return true;
  if (projectRole === 'owner') return true;

  const { role, hasPolicyInPath } = getEffectiveNodeAuthorityRole(nodes, node, user?.id);
  if (node?.id && hasPolicyInPath) {
    return subtreeRoleAllows(role, action);
  }

  return projectRole === 'editor' && action !== 'manage_policy';
}

export function canLinkDependencyNodes(params: {
  nodes: ProjectNode[];
  fromNode?: Partial<ProjectNode> | null;
  toNode?: Partial<ProjectNode> | null;
  projectRole?: string;
  user?: Pick<User, 'id' | 'role'> | null;
}): boolean {
  const { nodes, fromNode, toNode, projectRole, user } = params;
  if (!fromNode?.id || !toNode?.id) return false;
  if (fromNode.projectId && toNode.projectId && fromNode.projectId !== toNode.projectId) {
    return false;
  }
  if (user?.role === 'admin') return true;
  if (projectRole === 'owner') return true;

  const fromResolution = getEffectiveNodeAuthorityRole(nodes, fromNode, user?.id);
  const toResolution = getEffectiveNodeAuthorityRole(nodes, toNode, user?.id);
  if (!fromResolution.hasPolicyInPath && !toResolution.hasPolicyInPath) {
    return projectRole === 'editor';
  }

  const fromPolicyIds = authorizedPolicyNodeIds(nodes, fromNode, user?.id, 'update');
  const toPolicyIds = authorizedPolicyNodeIds(nodes, toNode, user?.id, 'update');
  return Array.from(fromPolicyIds).some((policyId) => toPolicyIds.has(policyId));
}

export function getAuthorityPolicyRoleEditState(params: {
  nodes: ProjectNode[];
  node?: Partial<ProjectNode> | null;
  targetUserId: string;
  currentRole?: NodeAuthorityRole | null;
  isAuthorityOwner?: boolean;
  projectRole?: string;
  user?: Pick<User, 'id' | 'role'> | null;
}): {
  canEdit: boolean;
  allowedRoles: Array<'' | NodeAuthorityRole>;
  reason?: 'no_permission' | 'self' | 'owner' | 'peer_or_higher';
} {
  const { nodes, node, targetUserId, currentRole, isAuthorityOwner, projectRole, user } = params;
  if (user?.role === 'admin' || projectRole === 'owner') {
    return { canEdit: true, allowedRoles: authorityPolicyRoleOptions };
  }

  if (!canActOnNode({ nodes, node, action: 'manage_policy', projectRole, user })) {
    return { canEdit: false, allowedRoles: [], reason: 'no_permission' };
  }
  if (targetUserId === user?.id) {
    return { canEdit: false, allowedRoles: [], reason: 'self' };
  }
  if (isAuthorityOwner) {
    return { canEdit: false, allowedRoles: [], reason: 'owner' };
  }

  const actorRole = getEffectiveNodeAuthorityRole(nodes, node, user?.id).role;
  const actorRank = actorRole ? authorityRoleRank[actorRole] : undefined;
  if (actorRank === undefined) {
    return { canEdit: false, allowedRoles: [], reason: 'no_permission' };
  }

  const currentRank = currentRole ? authorityRoleRank[currentRole] : undefined;
  if (currentRank !== undefined && currentRank >= actorRank) {
    return { canEdit: false, allowedRoles: [], reason: 'peer_or_higher' };
  }

  return {
    canEdit: true,
    allowedRoles: authorityPolicyRoleOptions.filter((role) => (
      role === '' || authorityRoleRank[role] < actorRank
    )),
  };
}
